import { CheckCircle, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { client } from '@/api/client'
import { AuthLayout } from '@/components/AuthLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth'

export function VerifyEmailPage() {
  const { t } = useTranslation('auth')
  const { token } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { updateParent, parent, isAuthenticated } = useAuthStore()
  const verificationAttempted = useRef(false)

  useEffect(() => {
    if (isAuthenticated && parent?.is_email_verified) {
      navigate('/', { replace: true })
      return
    }

    const verifyEmail = async () => {
      if (verificationAttempted.current) return
      verificationAttempted.current = true

      if (!token) {
        setErrorMessage(t('verification.error'))
        setStatus('error')
        return
      }

      try {
        const response = await client.post(`/auth/verify-email/${token}/`)

        if (response.data.success) {
          setStatus('success')
          updateParent(response.data.parent)
          setTimeout(() => navigate('/'), 3000)
        } else {
          let translationKey = 'verification.error'
          if (response.data.message.includes('already been used')) {
            translationKey = 'verification.alreadyUsed'
          } else if (response.data.message.includes('expired')) {
            translationKey = 'verification.expired'
          } else if (response.data.message.includes('Invalid verification link')) {
            translationKey = 'verification.invalid'
          }

          setErrorMessage(t(translationKey))
          setStatus('error')

          if (response.data.message.includes('no longer valid') ||
              response.data.message.includes('Invalid verification link')) {
            setTimeout(() => navigate('/verification-pending'), 3000)
          }
        }
      } catch (error: any) {
        let translationKey = 'verification.error'
        const msg = error.response?.data?.message || ''

        if (msg.includes('already been used')) {
          translationKey = 'verification.alreadyUsed'
        } else if (msg.includes('expired')) {
          translationKey = 'verification.expired'
        } else if (msg.includes('Invalid verification link')) {
          translationKey = 'verification.invalid'
        }

        setErrorMessage(t(translationKey))
        setStatus('error')
      }
    }

    verifyEmail()
  }, [token, navigate, t, updateParent, isAuthenticated, parent])

  return (
    <AuthLayout>
      <div className="flex justify-center mb-8">
        <img src="/images/logo.png" alt="The Ranch" className="h-20 w-auto" />
      </div>

      <Card>
        <CardContent className="py-8">
          {status === 'verifying' && (
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <h2 className="text-lg font-semibold">{t('verification.verifying')}</h2>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="rounded-full bg-emerald-100 p-3 w-fit mx-auto">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-emerald-800">{t('verification.success')}</h2>
              <p className="text-sm text-muted-foreground">{t('verification.redirectingHome')}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="rounded-full bg-destructive/10 p-3 w-fit mx-auto">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
              <h2 className="text-lg font-semibold text-destructive">{errorMessage}</h2>
              <Button onClick={() => navigate('/login')}>
                {t('register.loginLink')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
